import { t } from 'i18next';
import { camelCase, startCase, upperFirst } from 'lodash';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { DiscountApplyToTypes } from 'corporate/enums';
import { PricingSourceDiscountType, PricingSourceType } from 'pricing/enums';
import { ICostSourceData } from 'pricing/types';
import { ICustomerQuotePart } from 'quotes/types';

import BaseSourceHelper from './BaseSourceHelper';

const getLabel = (shortKey: string, longKey: string, defaultValue = '') => ({
  short: t(shortKey) || defaultValue,
  long: startCase(t(longKey)) || defaultValue,
});

const CostSourceHelper = {
  getPricingProfileName: (costSourceData: ICostSourceData) => {
    const pricingSource = BaseSourceHelper.getCostSource(costSourceData);

    return BaseSourceHelper.getPricingProfileName(pricingSource);
  },

  getPricingProfileUrl: (costSourceData: ICostSourceData) => {
    const pricingSource = BaseSourceHelper.getCostSource(costSourceData);

    return BaseSourceHelper.getPricingProfileUrl(pricingSource);
  },

  getTypeLabel: ({
    costSourceType,
    costQuantityDiscountsApplied = 0,
  }: {
    costSourceType?: PricingSourceType;
    costQuantityDiscountsApplied?: number;
  }) => {
    if (!costSourceType || PricingSourceType.UNSET === costSourceType) {
      return getLabel('', '', DEFAULT_PLACEHOLDER);
    }

    const shortKey = `labelCostSourceType${upperFirst(camelCase(costSourceType))}Short`;
    const longKey = `labelCostSourceType${upperFirst(camelCase(costSourceType))}Long`;

    return costSourceType === PricingSourceType.CORPORATE && costQuantityDiscountsApplied === 1
      ? getLabel(shortKey + 'QtyDiscounts', longKey + 'QtyDiscounts')
      : getLabel(shortKey, longKey);
  },

  getDiscountTypeLabel: ({
    costSourceDiscountType,
    costSourceDiscountApplyTo,
  }: {
    costSourceDiscountType?: PricingSourceDiscountType;
    costSourceDiscountApplyTo?: DiscountApplyToTypes;
  }) => {
    if (!costSourceDiscountType) {
      return getLabel('', '');
    }

    const shortKey = `labelDiscountType${upperFirst(camelCase(costSourceDiscountType))}Short`;
    const longKey = `labelDiscountType${upperFirst(camelCase(costSourceDiscountType))}Long`;

    if (PricingSourceDiscountType.FIXED_PRICE === costSourceDiscountType) {
      return getLabel(
        shortKey.replace(
          upperFirst(camelCase(PricingSourceDiscountType.FIXED_PRICE)),
          upperFirst(camelCase(PricingSourceDiscountType.FIXED_COST))
        ),

        longKey.replace(
          upperFirst(camelCase(PricingSourceDiscountType.FIXED_PRICE)),
          upperFirst(camelCase(PricingSourceDiscountType.FIXED_COST))
        )
      );
    }

    if (
      [
        PricingSourceDiscountType.DISCOUNT,
        PricingSourceDiscountType.VENDOR_QUOTE_PRICING_GROUP_DISCOUNT,
        PricingSourceDiscountType.CURRENT_QUOTE_PRICING_GROUP_DISCOUNT,
        PricingSourceDiscountType.VENDOR_QUOTE_PART_DISCOUNT,
        PricingSourceDiscountType.CURRENT_QUOTE_PART_DISCOUNT,
      ].includes(costSourceDiscountType)
    ) {
      return costSourceDiscountApplyTo === DiscountApplyToTypes.STD_COST
        ? getLabel(
            shortKey.replace('Short', 'OffStdCostShort'),
            longKey.replace('Long', 'OffStdCostLong')
          )
        : costSourceDiscountApplyTo === DiscountApplyToTypes.LIST_PRICE
        ? getLabel(
            shortKey.replace('Short', 'OffListPriceShort'),
            longKey.replace('Long', 'OffListPriceLong')
          )
        : getLabel('', '');
    }

    return getLabel(shortKey, longKey);
  },

  getVendorQuoteNumber: (quotePart: ICustomerQuotePart) => {
    if (!quotePart) {
      return;
    }

    return (
      quotePart?.costSourceQuoteExternalNumber ||
      quotePart?.costSourceQuoteId ||
      quotePart?.costSourceVendorQuoteNumber
    );
  },
};

export default CostSourceHelper;
